// ─────────────────────────────────────────────────────────────
// ClerkLoginPage — full-screen login overlay for the DAW
//
// Mounts Clerk's hosted sign-in component into a dark overlay
// before the DAW boots. Removed from the DOM once signed in.
// ─────────────────────────────────────────────────────────────

import "./ClerkLoginPage.sass"
import {ClerkAuth} from "./ClerkAuth"

let _overlay: HTMLElement | null = null
let _mountEl: HTMLElement | null = null

export namespace ClerkLoginPage {

    /**
     * Show the login page. Returns a Promise that resolves once
     * the user successfully signs in.
     */
    export const show = (): Promise<void> => {
        return new Promise(resolve => {

            // ── Build overlay ─────────────────────────────────
            _overlay = document.createElement("div")
            _overlay.className = "clerk-login-overlay"

            const card = document.createElement("div")
            card.className = "clerk-login-card"

            // Logo / brand header
            const brand = document.createElement("div")
            brand.className = "clerk-login-brand"
            brand.innerHTML = `
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" class="clerk-login-logo">
                    <circle cx="20" cy="20" r="20" fill="#7c3aed"/>
                    <path d="M12 20 L20 12 L28 20 L20 28 Z" fill="white" opacity="0.9"/>
                </svg>
                <span class="clerk-login-title">Sunday Triggers</span>
                <span class="clerk-login-subtitle">DAW Studio</span>
            `

            // Clerk sign-in mount point
            _mountEl = document.createElement("div")
            _mountEl.className = "clerk-login-mount"

            card.appendChild(brand)
            card.appendChild(_mountEl)
            _overlay.appendChild(card)
            document.body.appendChild(_overlay)

            // ── Mount Clerk sign-in component ─────────────────
            const clerk = ClerkAuth.getClerk()
            if (!clerk) {
                console.error("[ClerkLoginPage] ClerkAuth not initialized")
                return
            }

            clerk.mountSignIn(_mountEl, {
                // Appearance overrides to blend with dark DAW theme
                appearance: {
                    variables: {
                        colorPrimary: "#7c3aed",
                        colorBackground: "#1e1e2e",
                        colorInputBackground: "#2a2a3d",
                        colorInputText: "#e0e0f0",
                        colorText: "#e0e0f0",
                        colorTextSecondary: "#9090aa",
                        borderRadius: "8px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                    },
                    elements: {
                        card: "clerk-card-override",
                        formButtonPrimary: "clerk-btn-primary-override",
                    },
                },
            })

            // ── Wait for sign-in ──────────────────────────────
            const unsubscribe = ClerkAuth.onSessionChange(signedIn => {
                if (signedIn) {
                    unsubscribe()
                    hide()
                    resolve()
                }
            })

            // If somehow already signed in when this is called, resolve immediately
            if (ClerkAuth.isSignedIn()) {
                unsubscribe()
                hide()
                resolve()
            }
        })
    }

    /** Remove the login overlay from the DOM. */
    export const hide = (): void => {
        if (_mountEl) {
            ClerkAuth.getClerk()?.unmountSignIn(_mountEl)
            _mountEl = null
        }
        if (_overlay) {
            _overlay.remove()
            _overlay = null
        }
    }
}
